import { AUDIO_MAX_BYTES, AUDIO_MAX_SECONDS, formatDuration, type MediaStatusResponse } from "@kmstories/shared";
import { useEffect, useId, useRef, useState } from "react";
import { useT } from "../i18n/LocaleProvider.js";
import { api } from "../lib/api.js";
import type { FormId } from "../lib/useFormToken.js";

/**
 * In-browser voice notes. The container is chosen by asking the browser
 * (isTypeSupported ladder) and the file extension comes from what the
 * recorder reports AFTER construction — never from what we asked for. A plain
 * file input always sits beside the record button: it covers browsers without
 * MediaRecorder and lets iPhone users upload an existing Voice Memo.
 */
const MIME_LADDER = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4;codecs=mp4a.40.2", "audio/mp4"];

export function pickAudioMime(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of MIME_LADDER) if (MediaRecorder.isTypeSupported(m)) return m;
  return "";
}

export function extensionFor(mime: string): string {
  if (mime.includes("webm")) return "webm";
  if (mime.includes("mp4") || mime.includes("m4a") || mime.includes("aac")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("wav")) return "wav";
  if (mime.includes("mpeg") || mime.includes("mp3")) return "mp3";
  return "bin";
}

export type MediaState =
  | { phase: "idle" }
  | { phase: "recording"; seconds: number }
  | { phase: "preview"; blob: Blob; url: string; seconds: number }
  | { phase: "uploading"; progress: number }
  | { phase: "processing"; mediaId: string; since: number }
  | { phase: "ready"; mediaId: string; durationSeconds?: number }
  | { phase: "error"; message: string; retryable: boolean };

export function AudioRecorder({
  getToken,
  onReady,
  onReset,
  initial,
}: {
  getToken: (formId: FormId) => Promise<string>;
  onReady: (mediaId: string) => void;
  onReset: () => void;
  initial?: { mediaId: string } | null;
}) {
  const { m, locale } = useT();
  const id = useId();
  const [state, setState] = useState<MediaState>(initial ? { phase: "ready", mediaId: initial.mediaId } : { phase: "idle" });
  const [supported, setSupported] = useState<boolean>(true);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<number | null>(null);
  const stream = useRef<MediaStream | null>(null);

  useEffect(() => {
    setSupported(pickAudioMime() !== null && typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia));
  }, []);

  useEffect(() => () => stopTracks(), []);

  function stopTracks() {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  }

  async function start() {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = s;
      const mime = pickAudioMime() || undefined;
      const rec = new MediaRecorder(s, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      rec.onstop = () => {
        const type = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(chunks.current, { type });
        stopTracks();
        setState((cur) => ({ phase: "preview", blob, url: URL.createObjectURL(blob), seconds: cur.phase === "recording" ? cur.seconds : 0 }));
      };
      recorder.current = rec;
      rec.start();
      setState({ phase: "recording", seconds: 0 });
      timer.current = window.setInterval(() => {
        setState((cur) => {
          if (cur.phase !== "recording") return cur;
          const next = cur.seconds + 1;
          if (next >= AUDIO_MAX_SECONDS) {
            rec.stop();
            return cur;
          }
          return { phase: "recording", seconds: next };
        });
      }, 1000);
    } catch {
      setState({ phase: "error", message: m.share.recorder.denied, retryable: true });
    }
  }

  function stop() {
    recorder.current?.stop();
  }

  async function upload(blob: Blob, filename: string) {
    if (blob.size > AUDIO_MAX_BYTES) {
      setState({ phase: "error", message: m.share.uploader.tooLarge.replace("{max}", `${Math.round(AUDIO_MAX_BYTES / 1024 / 1024)} ${m.common.mb}`), retryable: true });
      return;
    }
    setState({ phase: "uploading", progress: 0 });
    try {
      const formToken = await getToken("upload");
      const fd = new FormData();
      fd.set("kind", "audio");
      fd.set("formToken", formToken);
      fd.set("file", blob, filename);
      const res = await api.postForm<{ mediaId: string }>("/api/upload/audio", fd);
      setState({ phase: "processing", mediaId: res.mediaId, since: Date.now() });
    } catch {
      setState({ phase: "error", message: m.share.uploader.failed, retryable: true });
    }
  }

  // Poll processing → ready/rejected; give up (with a human path) after 3 minutes.
  useEffect(() => {
    if (state.phase !== "processing") return;
    const { mediaId, since } = state;
    const h = window.setInterval(async () => {
      try {
        const st = await api.get<MediaStatusResponse>(`/api/upload/status/${mediaId}`);
        if (st.status === "ready") {
          setState({ phase: "ready", mediaId, durationSeconds: st.durationSeconds });
          onReady(mediaId);
        } else if (st.status === "rejected") {
          const msg = st.reason === "too_long" ? m.share.uploader.tooLong.replace("{max}", `${AUDIO_MAX_SECONDS / 60} ${m.common.minutes}`) : m.share.uploader.rejected;
          setState({ phase: "error", message: msg, retryable: true });
        } else if (Date.now() - since > 180_000) {
          setState({ phase: "error", message: m.share.uploader.stuck, retryable: true });
        }
      } catch {
        if (Date.now() - since > 180_000) setState({ phase: "error", message: m.share.uploader.stuck, retryable: true });
      }
    }, 2000);
    return () => window.clearInterval(h);
  }, [state, onReady, m]);

  function reset() {
    stopTracks();
    setState({ phase: "idle" });
    onReset();
  }

  const r = m.share.recorder;

  return (
    <div className="recorder" aria-live="polite">
      {state.phase === "idle" && (
        <div className="recorder__row">
          {supported ? (
            <button type="button" className="btn btn--record" onClick={start}>
              {r.start}
            </button>
          ) : (
            <p className="notice">{r.unsupported}</p>
          )}
          <label className="btn btn--ghost">
            {r.orUpload}
            <input
              className="file-input"
              type="file"
              accept="audio/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f, f.name);
              }}
            />
          </label>
        </div>
      )}
      {supported && state.phase === "idle" && <p className="field__hint">{r.permission}</p>}

      {state.phase === "recording" && (
        <div className="recorder__row">
          <span className="recorder__dot" aria-hidden="true" />
          <span className="recorder__time" aria-label={r.elapsed}>
            {formatDuration(state.seconds, locale)}
          </span>
          <span className="muted small">{r.remaining.replace("{time}", formatDuration(AUDIO_MAX_SECONDS - state.seconds, locale))}</span>
          <button type="button" className="btn btn--primary" onClick={stop}>
            {r.stop}
          </button>
        </div>
      )}

      {state.phase === "preview" && (
        <>
          <audio controls src={state.url} aria-label={r.preview} style={{ width: "100%" }} />
          <div className="recorder__row">
            <button type="button" className="btn btn--primary" onClick={() => void upload(state.blob, `voice-note.${extensionFor(state.blob.type)}`)}>
              {r.use}
            </button>
            <button type="button" className="btn btn--ghost" onClick={reset}>
              {r.rerecord}
            </button>
          </div>
        </>
      )}

      {state.phase === "uploading" && <p className="muted">{m.share.uploader.uploading}…</p>}
      {state.phase === "processing" && <p className="muted">{m.share.uploader.processing}…</p>}

      {state.phase === "ready" && (
        <div className="recorder__row">
          <span className="badge badge--ok">{m.share.uploader.done}</span>
          {state.durationSeconds != null && <span className="muted">{formatDuration(state.durationSeconds, locale)}</span>}
          <button type="button" className="btn btn--ghost btn--small" onClick={reset}>
            {r.rerecord}
          </button>
        </div>
      )}

      {state.phase === "error" && (
        <div className="notice notice--danger" role="alert" id={`${id}-err`}>
          <p style={{ margin: 0 }}>{state.message}</p>
          {state.retryable && (
            <button type="button" className="btn btn--ghost btn--small" style={{ marginBlockStart: "0.6rem" }} onClick={reset}>
              {m.common.retry}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
